/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import { IS_IOS, IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { IYAGIGO_IOS_STORE_URL, IYAGIGO_ANDROID_STORE_URL } from '@constant/iyagigo/variable';
import { dismissApplicationDownload } from '@hook/iyagigo/useNotification';
import ImageBlock from '@component/common/block/image-block';
import ImageTextBlock from '@component/common/block/image-text-block';
import TextBlock from '@component/common/block/text-block';
import TextButton from '@design-system/text-button';
import Gap from '@component/common/gap';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import otterBookLogo from '@asset/image/otter-book-logo.webp';
import appStoreBadge from '@asset/image/app-store-badge.svg';
import googlePlayBadge from '@asset/image/google-play-badge.png';

const ButtonContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  gap: ${size.MARGIN_1};
`;

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: 100%;
`;

const ApplicationDownloadToast: React.FC = () => {
  const dispatch = useTypedDispatch();

  const handleOpenApplicationClick = () => {
    window.open(IS_IOS ? IYAGIGO_IOS_STORE_URL : IYAGIGO_ANDROID_STORE_URL, '_blank');
  };

  const handleIosStoreClick = () => {
    window.open(IYAGIGO_IOS_STORE_URL, '_blank');
  };

  const handleAndroidStoreClick = () => {
    window.open(IYAGIGO_ANDROID_STORE_URL, '_blank');
  };

  const handleDismissClick = () => {
    dismissApplicationDownload();
    dispatch(modalActions.hideToast());
  };

  return (
    <Wrapper>
      <ImageTextBlock
        src={otterBookLogo}
        alt='이야기고'
        text={'이야기고'}
        design={{
          image: { height: '60px' },
          text: { size: 'big', color: color.BLACK_2, bold: true },
          box: { gap: '10px' },
        }}
      />
      <Gap height={size.MARGIN_1} />
      <TextBlock
        text={'매일 10분, <strong>이야기고 앱</strong>으로<br/>더 간편하게 공부해보세요'}
        design={{ text: { size: 'large', color: color.BLACK_2 } }}
      />
      <Gap height={size.MARGIN_2} />
      {IS_IYAGIGO_MOBILE_VIEW ? (
        <TextButton
          text={'앱에서 보기'}
          size='large'
          shape='pill'
          width='100%'
          handleClick={handleOpenApplicationClick}
        />
      ) : (
        <ButtonContainer>
          <ImageBlock
            src={appStoreBadge}
            alt='App Store'
            design={{ image: { height: '48px' }, box: { event: { cursor: 'pointer' } } }}
            click={{ handler: handleIosStoreClick }}
          />
          <ImageBlock
            src={googlePlayBadge}
            alt='Google Play'
            design={{ image: { height: '48px' }, box: { event: { cursor: 'pointer' } } }}
            click={{ handler: handleAndroidStoreClick }}
          />
        </ButtonContainer>
      )}
      <Gap height={size.MARGIN_2} />
      <TextBlock
        text={'오늘은 그냥 볼게요'}
        design={{ text: { size: 'medium', color: color.GRAY_1 }, box: { event: { cursor: 'pointer' } } }}
        click={{ handler: handleDismissClick }}
      />
    </Wrapper>
  );
};

export default ApplicationDownloadToast;
